import React, { useState, useEffect, useRef } from "react";
import { Icon, NativeSelect, Switch } from "../../";
import { useUpdateDriverGroup } from "../../../../hooks/useDriverCompanies";
import {
  secondsToTime,
  timeToSeconds,
  validateTimeInput,
  parseTimeInputs,
  ORDER_STATUS_OPTIONS,
  PRIORITY_OPTIONS,
} from "./utils";

const DriverGroupAssignmentSettings = ({ driverGroup, driverGroupId }) => {
  const [formData, setFormData] = useState({
    orders_group_start_in_status: 7,
    autoassign_autoassign_driver: false,
    autoreject_after_expiration: false,
    priority: 0,
  });

  const [timeInputs, setTimeInputs] = useState({
    hours: 0,
    minutes: 0,
    seconds: 30,
  });

  const updateMutation = useUpdateDriverGroup();
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (driverGroup) {
      setFormData({
        orders_group_start_in_status:
          driverGroup.orders_group_start_in_status ?? 7,
        autoassign_autoassign_driver:
          driverGroup.autoassign_autoassign_driver ?? false,
        autoreject_after_expiration:
          driverGroup.autoreject_after_expiration ?? false,
        priority: driverGroup.priority ?? 0,
      });

      // Convert autoreject time from seconds to hours/minutes/seconds
      const autorejectTime = driverGroup.autoassign_autoreject_time ?? 0;
      const time = secondsToTime(autorejectTime);
      setTimeInputs(time);
    }
  }, [driverGroup]);

  const handleChange = async (field, value) => {
    // Update local state optimistically
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Save immediately
    try {
      await updateMutation.mutateAsync({
        driverGroupId,
        data: {
          [field]: value,
        },
      });
    } catch (error) {
      // Revert on error
      if (driverGroup) {
        setFormData((prev) => ({
          ...prev,
          [field]: driverGroup[field],
        }));
      }
      console.error("Error updating driver group:", error);
    }
  };

  const handleTimeInputChange = (unit, value) => {
    const validation = validateTimeInput(value, unit);

    if (!validation.isValid) return;

    if (validation.isEmpty) {
      setTimeInputs((prev) => ({ ...prev, [unit]: "" }));
      return;
    }

    const updatedTimeInputs = {
      ...timeInputs,
      [unit]: validation.numValue,
    };

    setTimeInputs(updatedTimeInputs);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      const { hours, minutes, seconds } = parseTimeInputs(updatedTimeInputs);

      const totalSeconds = timeToSeconds(hours, minutes, seconds);

      try {
        await updateMutation.mutateAsync({
          driverGroupId,
          data: {
            autoassign_autoreject_time: totalSeconds,
          },
        });
      } catch {
        if (driverGroup) {
          const time = secondsToTime(
            driverGroup.autoassign_autoreject_time ?? 0,
          );
          setTimeInputs(time);
        }
      }
    }, 1000);
  };

  const handleTimeInputBlur = (unit) => {
    if (timeInputs[unit] === "") {
      setTimeInputs((prev) => ({ ...prev, [unit]: 0 }));
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="ordering-delivery-panel border-border bg-background rounded-lg border p-6">
      <h3 className="text-foreground mb-4 flex items-center text-lg font-semibold">
        <Icon name="Clock" className="text-muted-foreground mr-2 h-5 w-5" />
        When would you like this automatic assignment process to begin
      </h3>

      <div className="space-y-4">
        <div className="space-y-2">
          <label
            htmlFor={`driver-group-${driverGroupId}-start-status`}
            className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium"
          >
            Order Status
          </label>
          <NativeSelect
            id={`driver-group-${driverGroupId}-start-status`}
            value={formData.orders_group_start_in_status}
            onChange={(e) =>
              handleChange(
                "orders_group_start_in_status",
                parseInt(e.target.value),
              )
            }
            disabled={updateMutation.isPending}
            className="custom-select border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {ORDER_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
        </div>

        {/* Assign Automatically Toggle */}
        <div className="bg-card/50 flex items-center justify-between rounded-lg p-4">
          <div className="flex-1">
            <label className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium">
              Assign Automatically
            </label>
            <p className="text-muted-foreground mt-1 text-xs">
              Automatically assign drivers to orders when they reach the
              selected status
            </p>
          </div>
          <Switch
            checked={formData.autoassign_autoassign_driver}
            onCheckedChange={(checked) =>
              handleChange("autoassign_autoassign_driver", checked)
            }
            disabled={updateMutation.isPending}
          />
        </div>

        {/* Auto-reject After Expiration Toggle */}
        <div className="bg-card/50 flex items-center justify-between rounded-lg p-4">
          <div className="flex-1">
            <label className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium">
              Auto-reject After Expiration
            </label>
            <p className="text-muted-foreground mt-1 text-xs">
              Automatically reject orders after the expiration time
            </p>
          </div>
          <Switch
            checked={formData.autoreject_after_expiration}
            onCheckedChange={(checked) =>
              handleChange("autoreject_after_expiration", checked)
            }
            disabled={updateMutation.isPending}
          />
        </div>

        {/* Auto-reject Time Inputs */}
        <div className="space-y-2">
          <p className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium">
            Auto reject Order if not assign in
          </p>
          <div className="flex items-center space-x-2">
            {/* Hours Input */}
            <div className="flex-1">
              <input
                type="text"
                value={timeInputs.hours}
                onChange={(e) => handleTimeInputChange("hours", e.target.value)}
                onBlur={() => handleTimeInputBlur("hours")}
                disabled={updateMutation.isPending}
                className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 text-center focus:border-transparent focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="0"
              />
              <p className="text-muted-foreground mt-1 text-center text-xs">
                Hours
              </p>
            </div>

            <span className="text-muted-foreground pb-5 text-xl">:</span>

            {/* Minutes Input */}
            <div className="flex-1">
              <input
                type="text"
                value={timeInputs.minutes}
                onChange={(e) =>
                  handleTimeInputChange("minutes", e.target.value)
                }
                onBlur={() => handleTimeInputBlur("minutes")}
                disabled={updateMutation.isPending}
                className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 text-center focus:border-transparent focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="0"
              />
              <p className="text-muted-foreground mt-1 text-center text-xs">
                Minutes
              </p>
            </div>

            <span className="text-muted-foreground pb-5 text-xl">:</span>

            {/* Seconds Input */}
            <div className="flex-1">
              <input
                type="text"
                value={timeInputs.seconds}
                onChange={(e) =>
                  handleTimeInputChange("seconds", e.target.value)
                }
                onBlur={() => handleTimeInputBlur("seconds")}
                disabled={updateMutation.isPending}
                className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 text-center focus:border-transparent focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="0"
              />
              <p className="text-muted-foreground mt-1 text-center text-xs">
                Seconds
              </p>
            </div>
          </div>
          <p className="text-muted-foreground mt-2 text-xs">
            Time before automatically rejecting unassigned orders
          </p>
        </div>

        {/* Priority Select */}
        <div className="space-y-2">
          <label
            htmlFor={`driver-group-${driverGroupId}-priority`}
            className="text-foreground block text-[length:var(--font-size-product-ui)] font-medium"
          >
            Priority
          </label>
          <NativeSelect
            id={`driver-group-${driverGroupId}-priority`}
            value={formData.priority}
            onChange={(e) => handleChange("priority", parseInt(e.target.value))}
            disabled={updateMutation.isPending}
            className="custom-select border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 focus:border-transparent focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
          <p className="text-muted-foreground mt-1 text-xs">
            Set the priority level for this delivery service group
          </p>
        </div>
      </div>
    </div>
  );
};

export default DriverGroupAssignmentSettings;
